import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  CircularProgress,
  Divider
} from '@mui/material';
import { Send as SendIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../../utils/apiConfig';

/**
 * FormRenderer Component
 *
 * Dynamically renders a form based on JSON schema from the backend.
 * Supports all field types defined in the V2 forms system.
 */
export default function FormRenderer({ overrideLegacyId, overrideTemplateId }) {
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [template, setTemplate] = useState(null);
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId, overrideLegacyId, overrideTemplateId]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      const effectiveTemplateId = overrideTemplateId || routeId;
      let isLegacy = Boolean(overrideLegacyId) || (effectiveTemplateId && String(effectiveTemplateId).startsWith('legacy-')) || window.location.pathname.includes('/v2/forms/legacy/');
      let resp, data, tmpl, parsedSchema;

      if (isLegacy) {
        const legacyId = overrideLegacyId || window.location.pathname.split('/').pop();
        const employeeId = localStorage.getItem('employeeId');
        const qs = employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : '';
        resp = await fetch(`${API_URL}/vnext/forms/legacy/templates/${legacyId}${qs}`, {
          headers: { 
            Authorization: `Bearer ${token}`
          }
        });
        data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to load legacy form');
        tmpl = data.data;
        parsedSchema = tmpl.schema_json;
      } else {
        resp = await fetch(`${API_URL}/vnext/forms/templates/${effectiveTemplateId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        data = await resp.json();
        if (!resp.ok) throw new Error(data.error || 'Failed to load form');
        tmpl = data.data;
        parsedSchema = tmpl.schema_json ? JSON.parse(tmpl.schema_json) : null;
      }

      setTemplate(tmpl);
      setSchema(parsedSchema);

      // Initialize form data with initial values when available
      if (parsedSchema?.properties) {
        const initialData = {};
        Object.keys(parsedSchema.properties).forEach(key => {
          const prop = parsedSchema.properties[key];
          if (tmpl.initial_data && Object.prototype.hasOwnProperty.call(tmpl.initial_data, key)) {
            initialData[key] = tmpl.initial_data[key] ?? '';
          } else if (prop.type === 'boolean') {
            initialData[key] = false;
          } else if (prop.type === 'number') {
            initialData[key] = '';
          } else {
            initialData[key] = '';
          }
        });
        setFormData(initialData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));

    // Clear validation error for this field
    if (validationErrors[fieldId]) {
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[fieldId];
        return updated;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (schema?.required && Array.isArray(schema.required)) {
      schema.required.forEach(fieldId => {
        const value = formData[fieldId];
        if (value === undefined || value === null || value === '') {
          const fieldTitle = schema.properties[fieldId]?.title || fieldId;
          errors[fieldId] = `${fieldTitle} is required`;
        }
      });
    }

    // Type validation
    Object.keys(formData).forEach(fieldId => {
      const prop = schema?.properties?.[fieldId];
      const value = formData[fieldId];

      if (!prop || !value) return;

      // Email validation
      if (prop.format === 'email' && value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errors[fieldId] = 'Invalid email address';
        }
      }

      // Number validation
      if (prop.type === 'number' && value !== '') {
        const num = parseFloat(value);
        if (isNaN(num)) {
          errors[fieldId] = 'Must be a valid number';
        }
        if (prop.minimum !== undefined && num < prop.minimum) {
          errors[fieldId] = `Must be at least ${prop.minimum}`;
        }
        if (prop.maximum !== undefined && num > prop.maximum) {
          errors[fieldId] = `Must be at most ${prop.maximum}`;
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fix the validation errors before submitting');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');

      // Prepare responses
      const responses = {};
      Object.keys(formData).forEach(key => {
        const value = formData[key];
        // Convert number strings to actual numbers
        const prop = schema?.properties?.[key];
        if (prop?.type === 'number' && value !== '') {
          responses[key] = parseFloat(value);
        } else {
          responses[key] = value;
        }
      });

      const payload = {
        responses,
        signatures: [] // TODO: Add signature capture support
      };

      const effectiveTemplateId = overrideTemplateId || routeId;
      const isLegacy = Boolean(overrideLegacyId) || (effectiveTemplateId && String(effectiveTemplateId).startsWith('legacy-')) || window.location.pathname.includes('/v2/forms/legacy/');
      const legacyId = overrideLegacyId || window.location.pathname.split('/').pop();
      const submitUrl = isLegacy
        ? `${API_URL}/vnext/forms/legacy/templates/${legacyId}/submit`
        : `${API_URL}/vnext/forms/templates/${effectiveTemplateId}/submit`;

      const resp = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to submit form');

      setSuccess('Form submitted successfully!');

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/v2/forms');
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (fieldId, fieldSchema) => {
    const { type, title, description, format, enum: enumValues, minimum, maximum } = fieldSchema;
    const value = formData[fieldId] || '';
    const hasError = !!validationErrors[fieldId];
    const errorText = validationErrors[fieldId];

    // Text input (default)
    if (!format && !enumValues && (type === 'string' || !type)) {
      // Check if it's a multiline field (description or comments fields)
      const isMultiline = fieldId.includes('description') ||
                          fieldId.includes('comments') ||
                          fieldId.includes('statement') ||
                          fieldId.includes('what_') ||
                          title?.toLowerCase().includes('description');

      return (
        <TextField
          fullWidth
          label={title}
          value={value}
          onChange={(e) => handleChange(fieldId, e.target.value)}
          error={hasError}
          helperText={errorText || description}
          multiline={isMultiline}
          rows={isMultiline ? 4 : 1}
          required={schema?.required?.includes(fieldId)}
        />
      );
    }

    // Number input
    if (type === 'number') {
      return (
        <TextField
          fullWidth
          type="number"
          label={title}
          value={value}
          onChange={(e) => handleChange(fieldId, e.target.value)}
          error={hasError}
          helperText={errorText || description}
          required={schema?.required?.includes(fieldId)}
          inputProps={{
            min: minimum,
            max: maximum,
            step: fieldId.includes('rate') || fieldId.includes('amount') ? 0.01 : 1
          }}
        />
      );
    }

    // Date input
    if (format === 'date') {
      return (
        <TextField
          fullWidth
          type="date"
          label={title}
          value={value}
          onChange={(e) => handleChange(fieldId, e.target.value)}
          error={hasError}
          helperText={errorText || description}
          required={schema?.required?.includes(fieldId)}
          InputLabelProps={{ shrink: true }}
        />
      );
    }

    // Email input
    if (format === 'email') {
      return (
        <TextField
          fullWidth
          type="email"
          label={title}
          value={value}
          onChange={(e) => handleChange(fieldId, e.target.value)}
          error={hasError}
          helperText={errorText || description}
          required={schema?.required?.includes(fieldId)}
        />
      );
    }

    // Select (dropdown)
    if (enumValues && Array.isArray(enumValues)) {
      return (
        <FormControl fullWidth error={hasError} required={schema?.required?.includes(fieldId)}>
          <InputLabel>{title}</InputLabel>
          <Select
            value={value}
            label={title}
            onChange={(e) => handleChange(fieldId, e.target.value)}
          >
            <MenuItem value="">
              <em>-- Select --</em>
            </MenuItem>
            {enumValues.map(val => (
              <MenuItem key={val} value={val}>
                {val.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </MenuItem>
            ))}
          </Select>
          {(errorText || description) && (
            <Typography variant="caption" color={hasError ? 'error' : 'text.secondary'} sx={{ ml: 2, mt: 0.5 }}>
              {errorText || description}
            </Typography>
          )}
        </FormControl>
      );
    }

    // Boolean (checkbox)
    if (type === 'boolean') {
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!value}
              onChange={(e) => handleChange(fieldId, e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="body1">{title}</Typography>
              {description && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {description}
                </Typography>
              )}
            </Box>
          }
        />
      );
    }

    // Signature field (placeholder for now)
    if (fieldId.includes('signature')) {
      return (
        <Box>
          <TextField
            fullWidth
            label={title}
            value={value}
            onChange={(e) => handleChange(fieldId, e.target.value)}
            error={hasError}
            helperText={errorText || description || "Type your full name as signature"}
            required={schema?.required?.includes(fieldId)}
            placeholder="Type your full name"
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Note: Advanced e-signature capture coming soon
          </Typography>
        </Box>
      );
    }

    // Default fallback
    return (
      <TextField
        fullWidth
        label={title}
        value={value}
        onChange={(e) => handleChange(fieldId, e.target.value)}
        error={hasError}
        helperText={errorText || description}
        required={schema?.required?.includes(fieldId)}
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !template) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/v2/forms')}>
          Back to Forms
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/v2/forms')} sx={{ mb: 2 }}>
          Back to Forms
        </Button>
        <Typography variant="h4" gutterBottom>
          {template?.name || 'Form'}
        </Typography>
        {schema?.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {schema.description}
          </Typography>
        )}
        <Divider />
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Form */}
      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {schema?.properties && Object.entries(schema.properties).map(([fieldId, fieldSchema]) => (
            <Grid item xs={12} md={fieldSchema.type === 'boolean' || fieldId.includes('signature') ? 12 : 6} key={fieldId}>
              {renderField(fieldId, fieldSchema)}
            </Grid>
          ))}

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<SendIcon />}
                disabled={submitting || success}
              >
                {submitting ? 'Submitting...' : success ? 'Submitted!' : 'Submit Form'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/v2/forms')}
                disabled={submitting}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Footer Info */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Form ID: {template?.id || overrideTemplateId || routeId || (overrideLegacyId ? `legacy-${overrideLegacyId}` : '')} | Category: {template?.category} | Version: {template?.version || 1}
        </Typography>
      </Box>
    </Box>
  );
}
